import { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "sonner";
import { mui } from "@/mui";
import { 
  MicIcon, 
  VideoIcon, 
  DatabaseIcon, 
  FolderIcon,
  ShieldCheckIcon,
  AlertCircleIcon,
  CheckCircle2Icon,
  XCircleIcon,
  ScrollTextIcon,
  BellIcon
} from "lucide-react";

/**
 * Create bootstrap storage data to signal storage need to browser
 * This significantly improves Chrome's likelihood of granting persistent storage
 * 
 * Data created:
 * - User profile stub (for future KYC/2FA flow)
 * - Empty database structures
 * - OPFS file system initialization
 * - Welcome/tutorial content
 */
/**
 * Wait for Chrome to register storage activity in its quota system
 * Chrome's storage estimate updates asynchronously - we need to poll until it shows our data
 */
async function waitForStorageRegistration(minBytes: number, maxWaitMs: number = 3000): Promise<boolean> {
  if (!navigator.storage?.estimate) {
    return false; // Can't verify, assume registered
  }
  
  const startTime = Date.now();
  const pollInterval = 200; // Check every 200ms
  
  while (Date.now() - startTime < maxWaitMs) {
    try {
      const estimate = await navigator.storage.estimate();
      const currentUsage = estimate.usage || 0;
      
      if (currentUsage >= minBytes) {
        console.log(`[PERSISTENCE] Storage registered: ${(currentUsage / 1024 / 1024).toFixed(2)}MB detected`);
        return true;
      }
      
      // Wait before next check
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    } catch (error) {
      console.warn('[PERSISTENCE] Error checking storage estimate:', error);
      return false;
    }
  }
  
  // Timeout - Chrome hasn't registered our storage yet
  try {
    const estimate = await navigator.storage.estimate();
    console.warn(`[PERSISTENCE] Timeout waiting for storage registration. Current: ${(estimate.usage || 0 / 1024 / 1024).toFixed(2)}MB, Expected: ${(minBytes / 1024 / 1024).toFixed(2)}MB`);
  } catch {}
  
  return false;
}

let _bootstrapInFlight: Promise<void> | null = null;

async function createBootstrapStorageData(): Promise<void> {
  // Guard: prevent concurrent calls which race on OPFS handles and cause
  // InvalidStateError ("state cached in an interface object was made but
  // the state had changed since it was read from disk").
  if (_bootstrapInFlight) {
    console.log('[BOOTSTRAP] Skipping duplicate call — bootstrap already in progress');
    return _bootstrapInFlight;
  }
  _bootstrapInFlight = _createBootstrapStorageDataImpl();
  try {
    await _bootstrapInFlight;
  } finally {
    _bootstrapInFlight = null;
  }
}

async function _createBootstrapStorageDataImpl(): Promise<void> {
  const startTime = Date.now();
  
  // 1. Create IndexedDB structure with realistic schema
  const dbRequest = indexedDB.open('MobyNetBootstrap', 1);
  
  await new Promise<void>((resolve, reject) => {
    dbRequest.onerror = () => reject(dbRequest.error);
    dbRequest.onsuccess = () => resolve();
    
    dbRequest.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      // User profile store (stub for future KYC/2FA flow)
      if (!db.objectStoreNames.contains('userProfile')) {
        const profileStore = db.createObjectStore('userProfile', { keyPath: 'id' });
        profileStore.createIndex('email', 'email', { unique: false });
        profileStore.createIndex('phone', 'phone', { unique: false });
      }
      
      // Contacts store
      if (!db.objectStoreNames.contains('contacts')) {
        const contactsStore = db.createObjectStore('contacts', { keyPath: 'address' });
        contactsStore.createIndex('name', 'name', { unique: false });
      }
      
      // Settings store
      if (!db.objectStoreNames.contains('settings')) {
        db.createObjectStore('settings', { keyPath: 'key' });
      }
      
      // Burnable keys store (for future auth flow)
      if (!db.objectStoreNames.contains('burnableKeys')) {
        const keysStore = db.createObjectStore('burnableKeys', { keyPath: 'id', autoIncrement: true });
        keysStore.createIndex('createdAt', 'createdAt', { unique: false });
        keysStore.createIndex('expiresAt', 'expiresAt', { unique: false });
      }
    };
  });
  
  const db = dbRequest.result;
  
  // 2. Write initial data to IndexedDB (~5MB)
  const transaction = db.transaction(['userProfile', 'settings', 'burnableKeys'], 'readwrite');
  
  // Profile stub (future KYC/2FA integration point)
  const profileStore = transaction.objectStore('userProfile');
  await new Promise<void>((resolve, reject) => {
    const profileStub = {
      id: 'user_stub',
      version: 1,
      createdAt: Date.now(),
      // Stubs for future KYC/2FA flow
      kycStatus: 'pending', // 'pending' | 'verified' | 'failed'
      twoFactorEnabled: false,
      burnableKeyRotation: 'enabled',
      // Will store encrypted email/phone after KYC
      encryptedContactMethods: [],
      // Placeholder data to show storage intent
      preferences: new Array(1000).fill(0).map((_, i) => ({
        key: `pref_${i}`,
        value: `placeholder_${i}`,
        category: 'general'
      }))
    };
    
    const req = profileStore.put(profileStub);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
  
  // Settings with bootstrap data
  const settingsStore = transaction.objectStore('settings');
  const settings = [
    { key: 'app_version', value: '1.0.0', timestamp: Date.now() },
    { key: 'onboarding_completed', value: false, timestamp: Date.now() },
    { key: 'theme', value: 'dark', timestamp: Date.now() },
    { key: 'persistence_requested', value: true, timestamp: Date.now() },
    // Add padding data to signal serious storage intent
    ...new Array(100).fill(0).map((_, i) => ({
      key: `bootstrap_${i}`,
      value: new Array(100).fill(`data_${i}`).join(','),
      timestamp: Date.now()
    }))
  ];
  
  for (const setting of settings) {
    await new Promise<void>((resolve, reject) => {
      const req = settingsStore.put(setting);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }
  
  db.close();
  
  // 3. Create OPFS structure with bootstrap files (~5MB)
  if (navigator.storage?.getDirectory) {
    try {
      const opfsRoot = await navigator.storage.getDirectory();
      
      // Create directory structure
      const bootstrapDir = await opfsRoot.getDirectoryHandle('bootstrap', { create: true });
      const welcomeDir = await opfsRoot.getDirectoryHandle('welcome', { create: true });
      
      // Write bootstrap file (signals OPFS usage)
      const bootstrapFile = await bootstrapDir.getFileHandle('init.dat', { create: true });
      const writable = await bootstrapFile.createWritable();
      
      // Create ~3MB of structured data (not random - shows intent)
      const bootstrapData = new Uint8Array(3 * 1024 * 1024);
      const header = new TextEncoder().encode('MOBYNET_BOOTSTRAP_V1');
      bootstrapData.set(header, 0);
      
      // Fill with patterned data (easier to debug than random)
      for (let i = header.length; i < bootstrapData.length; i += 4) {
        const value = i % 256;
        bootstrapData[i] = value;
        bootstrapData[i + 1] = (value + 1) % 256;
        bootstrapData[i + 2] = (value + 2) % 256;
        bootstrapData[i + 3] = (value + 3) % 256;
      }
      
      await writable.write(bootstrapData);
      await writable.close();
      
      // Write welcome/tutorial content
      const welcomeFile = await welcomeDir.getFileHandle('tutorial.json', { create: true });
      const welcomeWritable = await welcomeFile.createWritable();
      const welcomeContent = {
        version: 1,
        tutorials: [
          { id: 'getting_started', title: 'Getting Started', completed: false },
          { id: 'send_message', title: 'Send Your First Message', completed: false },
          { id: 'send_file', title: 'Send Your First File', completed: false },
          { id: 'create_group', title: 'Create a Group Chat', completed: false },
          { id: 'enable_2fa', title: 'Enable Two-Factor Authentication', completed: false }
        ],
        // Pad with additional content
        metadata: new Array(1000).fill(0).map((_, i) => ({
          step: i,
          content: `Tutorial step ${i} content placeholder`,
          timestamp: Date.now()
        }))
      };
      
      await welcomeWritable.write(JSON.stringify(welcomeContent));
      await welcomeWritable.close();
      
      console.log(`[BOOTSTRAP] OPFS structure created in ${Date.now() - startTime}ms`);
    } catch (opfsError) {
      console.warn('[BOOTSTRAP] OPFS initialization failed:', opfsError);
      // Non-critical - IndexedDB data is sufficient
    }
  }
  
  // 4. LocalStorage markers
  localStorage.setItem('moby_persistence_request', Date.now().toString());
  localStorage.setItem('moby_app_version', '1.0.0');
  localStorage.setItem('moby_bootstrap_created', Date.now().toString());
  
  const totalTime = Date.now() - startTime;
  console.log(`[BOOTSTRAP]  Complete - created ~10MB of data in ${totalTime}ms`);
}
import { EULA_SECTIONS, EULA_KEY_POINTS, EULA_VERSION } from "@/lib/eula-content";
import {
  COUNTRIES,
  SUBDIVISIONS,
  hasSubdivisionData,
  isPlausibleSubdivisionText,
  getCountryByCode,
  getSubdivisionByCode,
} from "@/lib/locations";

interface PermissionStatus {
  microphone: 'granted' | 'denied' | 'prompt' | 'checking';
  camera: 'granted' | 'denied' | 'prompt' | 'checking';
  notifications: 'granted' | 'denied' | 'prompt' | 'checking';
  persistentStorage: 'granted' | 'denied' | 'prompt' | 'checking';
  nativeFileSystem: 'available' | 'unavailable' | 'checking';
}

export interface EulaSignatureData {
  fullName: string;
  // Structured location data. countryCode is ISO 3166-1 alpha-2.
  // subdivisionCode is ISO 3166-2 when available (US/CA/GB/AU);
  // otherwise null and subdivisionName holds the validated free-text entry.
  countryCode: string;
  countryName: string;
  subdivisionCode: string | null;
  subdivisionName: string;
  subdivisionInputType: "dropdown" | "text";
  // Version of the EULA text the user is signing against.
  eulaVersion: string;
  // Legacy free-text fields retained for backward compatibility with any
  // existing consumer of this interface. Populated from the structured
  // fields above. New code should prefer the structured fields.
  country: string;
  stateRegion: string;
}

interface OnboardingPermissionsProps {
  onComplete: (eulaSignature?: EulaSignatureData) => void;
  skipable?: boolean;
  isDrawer?: boolean; // For styling when used in drawer
}


export function OnboardingPermissions({ onComplete, skipable = false, isDrawer = false }: OnboardingPermissionsProps) {
  const [permissions, setPermissions] = useState<PermissionStatus>({
    microphone: 'checking',
    camera: 'checking',
    notifications: 'checking',
    persistentStorage: 'checking',
    nativeFileSystem: 'checking'
  });
  
  // Refs for cleanup: prevent orphaned timers calling setPermissions after unmount
  const mountedRef = useRef(true);
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup on unmount: cancel pending persist timer, mark unmounted
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (persistTimerRef.current !== null) {
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
      }
    };
  }, []);

  const [eulaAccepted, setEulaAccepted] = useState(false);
  const [showEulaContent, setShowEulaContent] = useState(true);  // Auto-open EULA
  const [isGrantingStorage, setIsGrantingStorage] = useState(false);
  
  // EULA electronic signature fields — structured location data replaces
  // the free-text country/state fields that were accepting entries like
  // "Your Face" / "Your Butt" into the audit trail.
  const [eulaFullName, setEulaFullName] = useState('');
  const [eulaCountryCode, setEulaCountryCode] = useState('');
  const [eulaSubdivisionCode, setEulaSubdivisionCode] = useState(''); // ISO 3166-2 when available
  const [eulaSubdivisionText, setEulaSubdivisionText] = useState(''); // free-text fallback
  
  // New state for EULA scroll tracking
  const [eulaScrolledToBottom, setEulaScrolledToBottom] = useState(false);
  const eulaContentRef = useRef<HTMLDivElement>(null);

  // Check existing permissions on mount
  useEffect(() => {
    checkExistingPermissions();
  }, []);

  const checkExistingPermissions = async () => {
    // Check microphone
    try {
      const micPermission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      setPermissions(prev => ({ ...prev, microphone: micPermission.state }));
    } catch {
      setPermissions(prev => ({ ...prev, microphone: 'prompt' }));
    }

    // Check camera
    try {
      const camPermission = await navigator.permissions.query({ name: 'camera' as PermissionName });
      setPermissions(prev => ({ ...prev, camera: camPermission.state }));
    } catch {
      setPermissions(prev => ({ ...prev, camera: 'prompt' }));
    }

    // Check notifications
    try {
      if ('Notification' in window) {
        const state = Notification.permission as 'granted' | 'denied' | 'default';
        setPermissions(prev => ({ ...prev, notifications: state === 'default' ? 'prompt' : state }));
      } else {
        setPermissions(prev => ({ ...prev, notifications: 'denied' }));
      }
    } catch {
      setPermissions(prev => ({ ...prev, notifications: 'prompt' }));
    }

    // Check persistent storage
    try {
      if (navigator.storage?.persisted) {
        const persisted = await navigator.storage.persisted();
        setPermissions(prev => ({ ...prev, persistentStorage: persisted ? 'granted' : 'prompt' }));
      } else {
        setPermissions(prev => ({ ...prev, persistentStorage: 'prompt' }));
      }
    } catch {
      setPermissions(prev => ({ ...prev, persistentStorage: 'prompt' }));
    }

    // Check native file system (extension) - only show if extension is installed
    const hasExtension = (window as any).mobyNetExtension?.isAvailable === true;
    if (hasExtension) {
      setPermissions(prev => ({ ...prev, nativeFileSystem: 'available' }));
    }
  };

  // EULA scroll tracking effect
  useEffect(() => {
    const handleScroll = () => {
      if (eulaContentRef.current && showEulaContent) {
        const { scrollTop, scrollHeight, clientHeight } = eulaContentRef.current;
        // Consider scrolled to bottom if within 5px (for rounding errors)
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5;
        setEulaScrolledToBottom(isAtBottom);
      }
    };

    const scrollElement = eulaContentRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
      // Check initial state
      handleScroll();
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [showEulaContent]);

  const requestMicrophonePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        }
      });
      stream.getTracks().forEach(track => track.stop());
      setPermissions(prev => ({ ...prev, microphone: 'granted' }));
      // Save to localStorage for provider to check
      localStorage.setItem('moby_media_permissions', 'granted');
      toast.success("Microphone permission granted");
    } catch (err) {
      setPermissions(prev => ({ ...prev, microphone: 'denied' }));
      toast.error("Microphone permission denied");
    }
  };

  const requestCameraPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(track => track.stop());
      setPermissions(prev => ({ ...prev, camera: 'granted' }));
      // Save to localStorage for provider to check
      localStorage.setItem('moby_media_permissions', 'granted');
      toast.success("Camera permission granted");
    } catch (err) {
      setPermissions(prev => ({ ...prev, camera: 'denied' }));
      toast.error("Camera permission denied");
    }
  };

  // Standalone persist retry — called AFTER notification grant, NOT inside a state updater.
  // Guarded by mountedRef so it's safe if the component unmounts before the timer fires.
  const tryPersistAfterNotificationGrant = () => {
    // Cancel any previous pending timer
    if (persistTimerRef.current !== null) {
      clearTimeout(persistTimerRef.current);
    }
    console.log('[PERSIST_FIX] Notification granted — scheduling persist() attempt...');
    persistTimerRef.current = setTimeout(async () => {
      persistTimerRef.current = null;
      if (!mountedRef.current) return;
      try {
        const alreadyPersisted = await navigator.storage.persisted();
        if (alreadyPersisted) {
          if (mountedRef.current) {
            setPermissions(prev => ({ ...prev, persistentStorage: 'granted' }));
            localStorage.setItem('moby_persistent_storage', 'granted');
          }
          return;
        }
        const granted = await navigator.storage.persist();
        if (!mountedRef.current) return;
        if (granted) {
          console.log('[PERSIST_FIX] persist() succeeded after notification grant!');
          setPermissions(prev => ({ ...prev, persistentStorage: 'granted' }));
          localStorage.setItem('moby_persistent_storage', 'granted');
        }
      } catch (e) {
        console.warn('[PERSIST_FIX] persist() after notification grant failed:', e);
      }
    }, 500);
  };

  // PERSIST_FIX: Notification permission is a KEY signal in Chrome's engagement
  // heuristic for granting persistent storage. Requesting it before persist()
  // dramatically increases the chance of persist() succeeding.
  const requestNotificationPermission = async () => {
    try {
      if (!('Notification' in window)) {
        setPermissions(prev => ({ ...prev, notifications: 'denied' }));
        return;
      }
      
      const result = await Notification.requestPermission();
      console.log('[NOTIFICATIONS] Permission result:', result);
      
      if (result === 'granted') {
        setPermissions(prev => ({ ...prev, notifications: 'granted' }));
        localStorage.setItem('moby_notification_permission', 'granted');
        toast.success("Notification permission granted");
        
        // Try persist() after explicit notification grant (boosts Chrome engagement heuristic)
        tryPersistAfterNotificationGrant();
      } else if (result === 'default') {
        // Browser handled silently (e.g. Edge on IP address, quiet mode)
        // Show checkmark but do NOT trigger persist retry — avoids expanding the
        // error surface to browsers that silently return 'default'
        setPermissions(prev => ({ ...prev, notifications: 'granted' }));
        localStorage.setItem('moby_notification_permission', 'granted');
        toast.success("Notification permission granted");
      } else if (result === 'denied') {
        setPermissions(prev => ({ ...prev, notifications: 'denied' }));
      }
    } catch (err) {
      console.error('[NOTIFICATIONS] Error:', err);
      setPermissions(prev => ({ ...prev, notifications: 'denied' }));
    }
  };

  const requestPersistentStorage = async () => {
    if (isGrantingStorage) return; // Prevent double-click
    setIsGrantingStorage(true);
    try {
      if (!navigator.storage?.persist) {
        toast.error("Persistent storage not supported in this browser");
        setPermissions(prev => ({ ...prev, persistentStorage: 'denied' }));
        return;
      }

      // Check if already persisted
      const alreadyPersisted = await navigator.storage.persisted();
      if (alreadyPersisted) {
        setPermissions(prev => ({ ...prev, persistentStorage: 'granted' }));
        toast.success("Persistent storage already granted");
        return;
      }

      // PERSIST_FIX: If notification permission hasn't been granted yet, request it first.
      // Chrome's engagement heuristic weighs notification permission heavily.
      if ('Notification' in window && Notification.permission === 'default') {
        console.log('[PERSISTENCE] Requesting notification permission first (boosts Chrome engagement heuristic)...');
        try {
          const notifResult = await Notification.requestPermission();
          console.log('[PERSISTENCE] Notification permission result:', notifResult);
          if (notifResult === 'granted' || notifResult === 'default') {
            // 'granted' or 'default' (browser handled silently) — treat as granted
            setPermissions(prev => ({ ...prev, notifications: 'granted' }));
            localStorage.setItem('moby_notification_permission', 'granted');
          }
          // Small delay for Chrome to register the permission change
          await new Promise(r => setTimeout(r, 300));
        } catch (e) {
          console.warn('[PERSISTENCE] Notification permission request failed (non-blocking):', e);
        }
      }

      // CRITICAL: Create actual storage data BEFORE requesting persistence
      // Chrome is more likely to grant if it sees real storage usage
      console.log('[PERSISTENCE] Creating bootstrap data to signal storage need...');
      
      try {
        await createBootstrapStorageData();
        console.log('[PERSISTENCE]  Bootstrap data created successfully');
      } catch (bootstrapError) {
        console.warn('[PERSISTENCE] Bootstrap data creation failed (non-critical):', bootstrapError);
        // Continue anyway - Firefox will still show prompt
      }

      // NEW: Wait for Chrome to REGISTER the storage (timing fix for Chrome 131+)
      // Chrome's quota system updates asynchronously - we need to wait until it "sees" our data
      console.log('[PERSISTENCE] Waiting for Chrome to register storage activity...');
      
      const storageRegistered = await waitForStorageRegistration(5 * 1024 * 1024); // Wait for at least 5MB to be recognized
      if (storageRegistered) {
        console.log('[PERSISTENCE]  Chrome has registered storage activity');
      } else {
        console.warn('[PERSISTENCE]  Chrome may not have registered storage yet (will try anyway)');
      }

      // Check Permissions API state (diagnostic for Chrome)
      if (navigator.permissions?.query) {
        try {
          const permissionStatus = await navigator.permissions.query({ name: 'persistent-storage' as any });
          console.log('[PERSISTENCE] Permission API state:', permissionStatus.state);
          
          // If already granted via Permissions API, should succeed
          if (permissionStatus.state === 'granted') {
            console.log('[PERSISTENCE] Permission API shows granted - calling persist()...');
            const granted = await navigator.storage.persist();
            if (granted) {
              setPermissions(prev => ({ ...prev, persistentStorage: 'granted' }));
              toast.success("Persistent storage granted");
              localStorage.setItem('moby_persistent_storage', 'granted');
              return;
            }
          }
        } catch (permError) {
          console.log('[PERSISTENCE] Permissions API not available for persistent-storage');
        }
      }

      // Try multiple times with exponential backoff
      let granted = false;
      for (let attempt = 0; attempt < 3 && !granted; attempt++) {
        if (attempt > 0) {
          // Exponential backoff: 2s, 4s
          const delay = Math.pow(2, attempt) * 1000;
          console.log(`[PERSISTENCE] Waiting ${delay}ms before retry ${attempt + 1}...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        
        granted = await navigator.storage.persist();
        
        if (granted) {
          console.log(`[PERSISTENCE]  Granted on attempt ${attempt + 1}`);
        } else {
          // Log storage estimate to help debug
          try {
            const estimate = await navigator.storage.estimate();
            console.log(`[PERSISTENCE] Attempt ${attempt + 1} denied - current storage: ${(estimate.usage || 0 / 1024 / 1024).toFixed(2)}MB / ${(estimate.quota || 0 / 1024 / 1024).toFixed(2)}MB`);
          } catch {
            console.log(`[PERSISTENCE] Attempt ${attempt + 1} denied, will retry...`);
          }
        }
      }

      if (granted) {
        setPermissions(prev => ({ ...prev, persistentStorage: 'granted' }));
        toast.success("Persistent storage granted");
        localStorage.setItem('moby_persistent_storage', 'granted');
      } else {
        setPermissions(prev => ({ ...prev, persistentStorage: 'denied' }));
        
        // Check why it might have failed
        const hasMediaPermissions = localStorage.getItem('moby_media_permissions') === 'granted';
        const isSecureContext = window.isSecureContext;
        
        if (!isSecureContext) {
          toast.error("Persistent storage requires HTTPS");
        } else if (!hasMediaPermissions) {
          toast.warning("Try granting other permissions first, then retry persistent storage");
        } else {
          // Browser denied based on its heuristics
          const isChrome = /Chrome/.test(navigator.userAgent) && !/Edg/.test(navigator.userAgent);
          
          if (isChrome) {
            // Chrome-specific guidance for recent versions
            console.log('[PERSISTENCE] Chrome denied - user can manually enable in Settings');
            console.log('[PERSISTENCE] Path: Settings → Privacy & security → Site settings → Additional content settings → On-device site data');
            
            toast.info(
              "Browser denied persistent storage. " +
              "You can manually enable it in Chrome Settings → Privacy & Security → Site Settings → On-device site data. " +
              "The app will still work normally.",
              { duration: 8000 }
            );
          } else {
            toast.info("Browser denied persistent storage. The app will still work normally.");
          }
        }
        
        localStorage.setItem('moby_persistent_storage', 'denied_soft');
      }
    } catch (err) {
      console.error('[PERSISTENCE] Error:', err);
      setPermissions(prev => ({ ...prev, persistentStorage: 'denied' }));
      toast.error("Could not request persistent storage");
    } finally {
      setIsGrantingStorage(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'granted':
      case 'available':
        return <CheckCircle2Icon className="text-success" size={20} />;
      case 'denied':
        return <XCircleIcon className="text-error" size={20} />;
      case 'checking':
        return <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />;
      default:
        return <AlertCircleIcon className="text-warning" size={20} />;
    }
  };

  const subdivisionUsesDropdown = useMemo(
    () => hasSubdivisionData(eulaCountryCode),
    [eulaCountryCode]
  );

  const canProceed = useMemo(() => {
    if (!eulaAccepted) return false;
    if (eulaFullName.trim().length < 2) return false;
    if (!eulaCountryCode) return false;
    if (subdivisionUsesDropdown) {
      return !!eulaSubdivisionCode;
    }
    return isPlausibleSubdivisionText(eulaSubdivisionText);
  }, [eulaAccepted, eulaFullName, eulaCountryCode, eulaSubdivisionCode, eulaSubdivisionText, subdivisionUsesDropdown]);

  const hasExtension = (window as any).mobyNetExtension?.isAvailable === true;
  
  return (
    <div className={`flex flex-col gap-6 ${isDrawer ? 'p-4' : 'p-6'} ${isDrawer ? '' : 'bg-surface-container rounded-lg'}`}>
      <div className="flex items-center gap-3 mb-4">
        <ShieldCheckIcon className="text-primary" size={32} />
        <div>
          <h2 className="text-title-lg text-on-surface">Terms & Optional Permissions</h2>
          <p className="text-body-md text-on-surface-variant">
            Accept the terms to get started. Permissions are optional and can be granted later when needed.
          </p>
        </div>
      </div>

      {/* Terms of Use Section */}
      <div className="border border-outline-variant rounded-lg p-4">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="accept-eula"
            checked={eulaAccepted}
            onChange={(e) => setEulaAccepted(e.target.checked)}
            disabled={!eulaScrolledToBottom}
            className="mt-1 accent-primary size-5"
          />
          <div className="flex-1">
            <label htmlFor="accept-eula" className="text-body-lg cursor-pointer">
              I accept the Terms of Use and Privacy Policy
            </label>
            {showEulaContent && !eulaScrolledToBottom && (
              <p className="text-warning text-body-sm mt-1">
              ⚠️ IMPORTANT LEGAL NOTICES — PLEASE READ CAREFULLY BEFORE USING THIS SERVICE ⚠HOW TO ACCEPT THIS AGREEMENT: YOU MUST SCROLL TO THE BOTTOM OF THIS AGREEMENT AND ELECTRONICALLY SIGN BY ENTERING YOUR FULL LEGAL NAME, COUNTRY, AND STATE/REGION.
              
                  TERMINATION: Company reserves the right to suspend or terminate your Account for cause or for convenience, with advance notice if required.
              
                  BETA SERVICES: This Service or certain features may be in beta form, provided without warranties.
              
                  ILLEGAL USE PROHIBITED: Use of this Service for any illegal purpose, including copyright infringement, is strictly prohibited and may result in immediate termination and legal action.
              
                  MODIFICATIONS: Company reserves the right to modify this Agreement and Service. Material changes might not be communicated with advance notice. It is your obligation to review the Terms and Conditions / EULA on a regular basis for updates and modifications and your continued use of the Services constitutes your agreement to the modified Terms / EULA.
              </p>
            )}
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={() => setShowEulaContent(!showEulaContent)}
                className="text-primary underline text-body-sm"
              >
                {showEulaContent ? 'Hide' : 'Read'} Terms
              </button>
              <a
                href="https://becca1972.wixstudio.com/mobynet/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline text-body-sm"
              >
                Open in New Tab
              </a>
            </div>
            {showEulaContent && (
              <div 
                ref={eulaContentRef}
                className="mt-3 p-4 bg-surface rounded-lg max-h-64 overflow-y-auto text-body-sm border border-outline"
                style={{ 
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  maxWidth: '100%'
                }}
              >
                {/* Quick summary at top */}
                <div className="mb-4 p-3 bg-primary-container/10 rounded">
                  <h4 className="font-semibold mb-2 text-on-surface">Key Points:</h4>
                  <ul className="list-disc ml-5 space-y-1 text-on-surface-variant">
                    {EULA_KEY_POINTS.map((point, index) => (
                      <li key={index}>{point}</li>
                    ))}
                  </ul>
                </div>

                {/* Full EULA sections */}
                <div className="space-y-4">
                  {EULA_SECTIONS.map((section, index) => (
                    <div key={index} className="pb-3 border-b border-outline-variant last:border-0">
                      <h5 className="font-semibold mb-2 text-on-surface">{section.title}</h5>
                      <p className="text-on-surface-variant whitespace-pre-wrap">{section.content}</p>
                    </div>
                  ))}
                </div>

                {/* Scroll indicator at bottom */}
                <div className="mt-6 pt-3 border-t border-outline-variant sticky bottom-0 bg-surface">
                  {eulaScrolledToBottom ? (
                    <div className="flex items-center gap-2 text-success">
                      <CheckCircle2Icon size={16} />
                      <span className="font-medium">You have read all terms</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-warning animate-pulse">
                      <ScrollTextIcon size={16} />
                      <span className="font-medium">↓ Please scroll to read all terms</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Electronic Signature Fields — shown after EULA accepted */}
      {eulaAccepted && (
        <div className="border border-primary/30 rounded-lg p-4 bg-primary-container/5">
          <h3 className="text-body-lg font-medium text-on-surface mb-3">Electronic Signature</h3>
          <p className="text-body-sm text-on-surface-variant mb-4">
            "By entering your full legal name, country, and state/region below and clicking 'I Accept,' you are (1) signing this End User License Agreement electronically, which has the same legal effect as a handwritten signature; (2) declaring under penalty of perjury that the information you have provided is true, correct, and complete; and (3) representing that you are authorized to enter into this legally binding Agreement. This Agreement automatically governs your use of all current and future versions and tiers of the Service."
          </p>
          <div className="grid gap-3">
            <div>
              <label htmlFor="eula-fullname" className="text-label-sm text-on-surface-variant block mb-1">Full Legal Name *</label>
              <input
                id="eula-fullname"
                type="text"
                value={eulaFullName}
                onChange={(e) => setEulaFullName(e.target.value)}
                placeholder="Enter your full legal name"
                maxLength={120}
                className="w-full px-3 py-2 rounded border border-outline bg-surface text-on-surface text-body-sm"
                autoComplete="name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="eula-country" className="text-label-sm text-on-surface-variant block mb-1">Country *</label>
                <select
                  id="eula-country"
                  value={eulaCountryCode}
                  onChange={(e) => {
                    setEulaCountryCode(e.target.value);
                    // Reset subdivision whenever country changes so we don't
                    // retain a stale US state selected under a different country.
                    setEulaSubdivisionCode('');
                    setEulaSubdivisionText('');
                  }}
                  className="w-full px-3 py-2 rounded border border-outline bg-surface text-on-surface text-body-sm"
                  autoComplete="country"
                >
                  <option value="">Select country…</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="eula-state" className="text-label-sm text-on-surface-variant block mb-1">
                  {subdivisionUsesDropdown ? "State / Province / Region *" : "Region / Province *"}
                </label>
                {subdivisionUsesDropdown ? (
                  <select
                    id="eula-state"
                    value={eulaSubdivisionCode}
                    onChange={(e) => setEulaSubdivisionCode(e.target.value)}
                    disabled={!eulaCountryCode}
                    className="w-full px-3 py-2 rounded border border-outline bg-surface text-on-surface text-body-sm disabled:opacity-60"
                    autoComplete="address-level1"
                  >
                    <option value="">Select…</option>
                    {(SUBDIVISIONS[eulaCountryCode] || []).map((s) => (
                      <option key={s.code} value={s.code}>{s.name}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="eula-state"
                    type="text"
                    value={eulaSubdivisionText}
                    onChange={(e) => setEulaSubdivisionText(e.target.value)}
                    placeholder="Enter your region / province"
                    disabled={!eulaCountryCode}
                    maxLength={60}
                    className="w-full px-3 py-2 rounded border border-outline bg-surface text-on-surface text-body-sm disabled:opacity-60"
                    autoComplete="address-level1"
                  />
                )}
              </div>
            </div>
            {eulaCountryCode && !subdivisionUsesDropdown && eulaSubdivisionText.length > 0 && !isPlausibleSubdivisionText(eulaSubdivisionText) && (
              <p className="text-error text-body-sm">
                Please enter a valid region or province name (letters, spaces, hyphens; no test placeholders).
              </p>
            )}
            <p className="text-on-surface-variant text-body-sm">
              You are signing the EULA, version <strong>{EULA_VERSION}</strong>.
            </p>
          </div>
        </div>
      )}

      {/* Permissions Grid */}
      <div className="grid gap-4">
        {/* Microphone Permission */}
        <div className="border border-outline-variant rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MicIcon className="text-on-surface-variant" size={24} />
              <div>
                <h3 className="text-body-lg font-medium">Microphone Access <span className="text-body-sm text-on-surface-variant font-normal">(optional)</span></h3>
                <p className="text-body-sm text-on-surface-variant">
                  Enables voice calls and peer-to-peer connections. You'll be prompted again when you make your first call if skipped.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(permissions.microphone)}
              {permissions.microphone === 'prompt' && (
                <mui.Button 
                  onClick={requestMicrophonePermission}
                  variant="primary"
                >
                  Grant
                </mui.Button>
              )}
              {permissions.microphone === 'denied' && (
                <mui.Button 
                  onClick={() => {
                    setPermissions(prev => ({ ...prev, microphone: 'prompt' }));
                    requestMicrophonePermission();
                  }}
                  variant="secondary"
                >
                  Retry
                </mui.Button>
              )}
            </div>
          </div>
        </div>

        {/* Camera Permission */}
        <div className="border border-outline-variant rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <VideoIcon className="text-on-surface-variant" size={24} />
              <div>
                <h3 className="text-body-lg font-medium">Camera Access <span className="text-body-sm text-on-surface-variant font-normal">(optional)</span></h3>
                <p className="text-body-sm text-on-surface-variant">
                  Enables video streaming between peers. You'll be prompted when you first enable video if skipped.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(permissions.camera)}
              {permissions.camera === 'prompt' && (
                <mui.Button 
                  onClick={requestCameraPermission}
                  variant="secondary"
                >
                  Grant
                </mui.Button>
              )}
              {permissions.camera === 'denied' && (
                <mui.Button 
                  onClick={() => {
                    setPermissions(prev => ({ ...prev, camera: 'prompt' }));
                    requestCameraPermission();
                  }}
                  variant="secondary"
                >
                  Retry
                </mui.Button>
              )}
            </div>
          </div>
        </div>

        {/* Notification Permission — boosts Chrome engagement score for persist() */}
        <div className="border border-outline-variant rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BellIcon className="text-on-surface-variant" size={24} />
              <div>
                <h3 className="text-body-lg font-medium">Notifications <span className="text-body-sm text-on-surface-variant font-normal">(recommended)</span></h3>
                <p className="text-body-sm text-on-surface-variant">
                  Receive alerts for incoming messages and file transfers. Also helps the browser trust this site with persistent storage.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(permissions.notifications)}
              {permissions.notifications === 'prompt' && (
                <mui.Button 
                  onClick={requestNotificationPermission}
                  variant="primary"
                >
                  Grant
                </mui.Button>
              )}
              {permissions.notifications === 'denied' && (
                <span className="text-body-sm text-on-surface-variant">Blocked in browser settings</span>
              )}
            </div>
          </div>
        </div>

        {/* Persistent Storage */}
        <div className="border border-outline-variant rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DatabaseIcon className="text-on-surface-variant" size={24} />
              <div>
                <h3 className="text-body-lg font-medium">Persistent Storage <span className="text-body-sm text-on-surface-variant font-normal">(optional)</span></h3>
                <p className="text-body-sm text-on-surface-variant">
                  Protects your messages and files from browser cleanup. Granting notifications first greatly improves success. This will be retried automatically on future visits.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(permissions.persistentStorage)}
              {permissions.persistentStorage === 'prompt' && (
                <mui.Button 
                  onClick={requestPersistentStorage}
                  variant="secondary"
                  disabled={isGrantingStorage}
                >
                  {isGrantingStorage ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin inline-block h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                      Granting…
                    </span>
                  ) : 'Grant'}
                </mui.Button>
              )}
              {permissions.persistentStorage === 'denied' && (
                <mui.Button 
                  onClick={() => {
                    setPermissions(prev => ({ ...prev, persistentStorage: 'prompt' }));
                    requestPersistentStorage();
                  }}
                  variant="secondary"
                  disabled={isGrantingStorage}
                >
                  {isGrantingStorage ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin inline-block h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                      Granting…
                    </span>
                  ) : 'Retry'}
                </mui.Button>
              )}
            </div>
          </div>
        </div>

        {/* Native File System (Extension) - Only show if extension is installed */}
        {hasExtension && (
          <div className="border border-outline-variant rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FolderIcon className="text-on-surface-variant" size={24} />
                <div>
                  <h3 className="text-body-lg font-medium">Native File System</h3>
                  <p className="text-body-sm text-on-surface-variant">
                    Extension detected - Large file support enabled
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getStatusIcon('available')}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-primary-container/10 rounded-lg p-4">
        <h4 className="text-body-md font-medium mb-2 text-on-surface">Why these permissions?</h4>
        <ul className="text-body-sm text-on-surface-variant space-y-1">
          <li>• <strong>Microphone:</strong> Needed for voice calls and peer connections — you'll be prompted when making your first call</li>
          <li>• <strong>Camera:</strong> Needed for video streaming — you'll be prompted when enabling video</li>
          <li>• <strong>Notifications:</strong> Alerts for messages and transfers — also helps the browser grant persistent storage</li>
          <li>• <strong>Storage:</strong> Keeps your data safe — grant notifications first for best results. Automatically retried on future visits</li>
          {hasExtension && <li>• <strong>Extension:</strong> Enables direct file system access for massive file transfers</li>}
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center mt-4">
        <div className="text-body-sm text-on-surface-variant">
          {!canProceed && (
            !eulaAccepted
              ? "Please accept the Terms of Use to continue"
              : "Please complete your electronic signature (full name, country, region)"
          )}
        </div>
        <div className="flex gap-3">
          {skipable && (
            <mui.Button 
              onClick={() => onComplete()}
              variant="secondary"
            >
              Skip for Now
            </mui.Button>
          )}
          <mui.Button 
            onClick={() => {
              const country = getCountryByCode(eulaCountryCode);
              let subdivisionCode: string | null = null;
              let subdivisionName = '';
              let subdivisionInputType: 'dropdown' | 'text' = 'text';
              if (subdivisionUsesDropdown) {
                const sub = getSubdivisionByCode(eulaCountryCode, eulaSubdivisionCode);
                if (sub) {
                  subdivisionCode = sub.code;
                  subdivisionName = sub.name;
                  subdivisionInputType = 'dropdown';
                }
              } else {
                subdivisionName = eulaSubdivisionText.trim();
                subdivisionInputType = 'text';
              }
              const signature: EulaSignatureData = {
                fullName: eulaFullName.trim(),
                countryCode: eulaCountryCode,
                countryName: country?.name || eulaCountryCode,
                subdivisionCode,
                subdivisionName,
                subdivisionInputType,
                eulaVersion: EULA_VERSION,
                // Legacy shape populated for backward compatibility.
                country: country?.name || eulaCountryCode,
                stateRegion: subdivisionName,
              };
              onComplete(signature);
            }}
            variant="primary"
            filled
            disabled={!canProceed}
          >
            Continue
          </mui.Button>
        </div>
      </div>
    </div>
  );
}
